import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Package, Phone, Mail, MapPin, Loader2, XCircle } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { safeStorage } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import InstallationScheduler from '../components/InstallationScheduler';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Shown until the order-specific steps load; never promises measuring or installation
const NEUTRAL_STEPS = [
  "You'll receive an order confirmation email shortly.",
  "We'll review your order and contact you if we need any clarification.",
  "You'll receive updates as your custom order moves through production.",
];


const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const [showInstallationScheduler, setShowInstallationScheduler] = useState(false);
  const [installationSkipped, setInstallationSkipped] = useState(false);
  const { token, isAuthenticated } = useAuth();

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollPaymentStatus = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/checkout/status/${sessionId}`);
        setPaymentData(response.data);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          // Clear cart on successful payment
          safeStorage.removeItem('apollo_cart');
        } else if (response.data.status === 'expired') {
          setStatus('expired');
        } else if (pollCount < 5) {
          // Continue polling
          setTimeout(() => setPollCount(c => c + 1), 2000);
        } else {
          setStatus('pending');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (pollCount < 3) {
          setTimeout(() => setPollCount(c => c + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, pollCount]);

  if (status === 'loading') {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center pt-20">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center"
          >
            <Loader2 className="w-16 h-16 text-amber-600 animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-semibold mb-2">Processing Your Payment</h2>
            <p className="text-neutral-600">Please wait while we confirm your order...</p>
          </motion.div>
        </div>
      </Layout>
    );
  }

  if (status === 'error' || status === 'expired') {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center pt-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-md"
          >
            <XCircle className="w-20 h-20 text-red-500 mx-auto mb-6" />
            <h1 className="text-3xl font-semibold mb-4">Payment Issue</h1>
            <p className="text-neutral-600 mb-8">
              {status === 'expired' 
                ? 'Your payment session has expired. Please try again.'
                : 'We could not verify your payment. Please contact us if you believe this is an error.'}
            </p>
            <div className="flex gap-4 justify-center">
              <Link to="/quote">
                <Button className="bg-neutral-900 text-white hover:bg-neutral-800">
                  Return to Cart
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline">
                  Contact Support
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen pt-20 pb-20" data-testid="checkout-success-page">
        <div className="container-xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
            >
              <CheckCircle className="w-24 h-24 text-green-500 mx-auto mb-6" />
            </motion.div>
            
            <h1 className="text-4xl font-semibold mb-4">Thank You for Your Order!</h1>
            <p className="text-xl text-neutral-600 mb-8">
              Your custom window treatments are being prepared.
            </p>

            {paymentData && (
              <div className="bg-neutral-50 rounded-lg p-6 mb-8 text-left">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Order Details
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Order Total:</span>
                    <span className="font-medium">${(paymentData.amount_total / 100).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Payment Status:</span>
                    <span className="font-medium text-green-600 capitalize">{paymentData.payment_status}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Confirmation:</span>
                    <span className="font-mono text-xs">{sessionId?.slice(0, 20)}...</span>
                  </div>
                </div>
              </div>
            )}

            {/* Installation Scheduler Section */}
            {isAuthenticated && paymentData?.order_id && ['install', 'mixed'].includes(paymentData?.fulfillment_mode) && !installationSkipped && (
              <div className="mb-8 text-left">
                {!showInstallationScheduler ? (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6"
                  >
                    <h3 className="font-semibold text-lg mb-2">Professional Installation Available</h3>
                    <p className="text-neutral-600 mb-4">
                      Would you like to schedule professional installation for your new window treatments?
                    </p>
                    <div className="flex gap-3">
                      <Button
                        onClick={() => setShowInstallationScheduler(true)}
                        className="bg-amber-600 hover:bg-amber-700"
                      >
                        Schedule Installation
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setInstallationSkipped(true)}
                      >
                        Maybe Later
                      </Button>
                    </div>
                  </motion.div>
                ) : (
                  <InstallationScheduler
                    orderId={paymentData.order_id}
                    token={token}
                    shippingAddress={paymentData.shipping_address || {}}
                    onComplete={() => setInstallationSkipped(true)}
                    onSkip={() => setInstallationSkipped(true)}
                  />
                )}
              </div>
            )}

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-8 text-left">
              <h3 className="font-semibold mb-3">What Happens Next?</h3>
              <ol className="space-y-3 text-sm text-neutral-700" data-testid="checkout-next-steps" data-mode={paymentData?.fulfillment_mode || 'neutral'}>
                {(paymentData?.next_steps || NEUTRAL_STEPS).map((step, i) => (
                  <li key={step} className="flex gap-3" data-testid={`checkout-next-step-${i + 1}`}>
                    <span className="flex-shrink-0 w-6 h-6 bg-amber-600 text-white rounded-full flex items-center justify-center text-xs">{i + 1}</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </div>

            <div className="grid sm:grid-cols-3 gap-4 mb-8 text-sm">
              <div className="flex items-center gap-2 justify-center text-neutral-600">
                <Phone className="w-4 h-4" />
                <a href="tel:+12122090654" className="hover:underline" data-testid="checkout-contact-phone">(212) 209-0654</a>
              </div>
              <div className="flex items-center gap-2 justify-center text-neutral-600">
                <Mail className="w-4 h-4" />
                <a href="mailto:info@apolloshades.com" className="hover:underline" data-testid="checkout-contact-email">info@apolloshades.com</a>
              </div>
              <div className="flex items-center gap-2 justify-center text-neutral-600">
                <MapPin className="w-4 h-4" />
                <span>Brooklyn, NY</span>
              </div>
            </div>

            <div className="flex gap-4 justify-center">
              <Link to="/">
                <Button className="bg-neutral-900 text-white hover:bg-neutral-800">
                  Return Home
                </Button>
              </Link>
              <Link to="/products">
                <Button variant="outline">
                  Continue Shopping
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
};

export default CheckoutSuccessPage;
